import $ from 'jquery';
import CustomEvent from 'custom-event';
import {
  getWishlistItemData,
  formatProduct,
  getProductDetails,
  dispatchWishlistUpdate,
} from './helpers';

const _namespace = 'wishlist';

const productsListTemplate = `
<div class="product-listing">
{{^products}}
  {{emptyMessage}}
{{/products}}
<ul class="product-listing__list product-listing__list--center">
  {{#products}}
  <li class="product-listing__item"
    data-product 
    data-product-id="{{empi}}" 
    data-url="{{du}}" 
    data-variant-id="{{epi}}" 
    data-price="{{price}}"
    data-image="{{iu}}"
  >
    <div class="product-card">
      <button class="wishlist__remove button--transparent" data-remove>
        <svg class="icon"><use href="#cross" xlink:href="#cross"/></svg>
      </button>

      <a href="{{cprops.link}}">
        <div class="product-card__image-container">
          <div class="product-card__image-wrapper js">
            <img class="lazyautosizes" src="{{iu}}" />
          </div>
        </div>
      </a>

      <div class="product-card__footer" aria-hidden="true">
        <div class="product-card__title">
          <a href="{{cprops.link}}">{{cprops.title}}{{#cprops.options}} - {{cprops.options}}{{/cprops.options}}</a>
        </div>
        <span class="product-card__price" data-price="">
          <span data-sale-price="">
            {{formattedPrice}}
          </span>
        </span>

        {{^cprops.purchasable}}
          <div class="product-card__form">
            <a href="{{du}}" class="button button--small button--secondary product-card__action wishlist__action">
              {{chooseLabel}}
            </a>
          </div>
        {{/cprops.purchasable}}

        {{#cprops.purchasable}}
          {{#available}}
            <div class="product-card__form">
              <a href="{{du}}" class="button button--small button--secondary product-card__action wishlist__action">
                {{chooseLabel}}
              </a>
            </div>
          {{/available}}
        {{/cprops.purchasable}}
      </div>
    </li>
    {{/products}}
  </ul>
  </div>
`;

/**
 * @param {HTMLElement} element
 * @param {Object} opts
 */
export function wishlistProducts(element, opts = {}) {
  const defaults = {
    addButton: '[data-add-button]',
    addForm: '[data-add-to-cart]',
    product: '[data-product]',
    removeButton: '[data-remove]',
    addingText: 'Adding...',
    addedText: 'Added',
    select: '[data-select]',
    emptyMessage: 'You currently have no products in your wishlist',
    selectLabel: 'Select size',
    moveButtonLabel: 'Move to Bag',
    chooseLabel: 'Choose Option',
    errorMessage:
      'We are currently unable to show your wishlist. We apologize for any inconvenience this may cause.',
  };

  if (!element) {
    return null;
  }

  const options = {
    ...defaults,
    ...opts,
    ...element.dataset,
  };

  if (!window.SwymCallbacks) {
    window.SwymCallbacks = [];
  }

  window.SwymCallbacks.push(productsCallback);

  function bindEventListeners() {
    element.on('submit.wishlist.add', options.addForm, handleAddCartClick);
    element.on(
      'click.wishlist.remove',
      options.removeButton,
      handleRemoveClick,
    );
    element.on(`${_namespace}:removeProduct`, options.product, handleRemove);
  }

  function removeEventListeners() {
    element.off('submit.wishlist.add');
    element.off('click.wishlist.remove');
    element.off(`${_namespace}:removeProduct`);
  }

  function handleRemove(event) {
    const {detail = {}} = event;
    const {product} = detail;

    if (!product) {
      return;
    }

    removeFromWishlist(product);
  }

  /**
   * @param {Event} event
   */
  function handleRemoveClick(event) {
    event.preventDefault();
    const $target = $(event.target);
    const $product = $target.closest(options.product);

    removeFromWishlist($product);
  }

  /**
   * @param {Event} event
   */
  function handleAddCartClick(event) {
    event.preventDefault();
    const $target = $(event.target);
    const $product = $target.closest(options.product);

    addProductToCart($product);
  }

  /**
   * @param {Object} product
   */
  function addProductToCart(product) {
    const {empi, du} = getWishlistItemData(product);
    const $select = $(product).find(options.select);
    const epi = $select.val();
    const $button = $(product).find(options.addButton);
    const button = $button.get(0);

    $button.html(options.addingText);
    window._swat.replayAddToCart(
      {
        empi,
        du,
      },
      epi,
      () => {
        $button.html(options.addedText);

        const removeEvent = new CustomEvent(`${_namespace}:removeProduct`, {
          bubbles: true,
          detail: {
            product,
          },
        });
        button.dispatchEvent(removeEvent);

        const updatedEvent = new CustomEvent('cart:updated');
        document.dispatchEvent(updatedEvent);
      },
      window.console.log,
    );
  }

  /**
   * @param {jQuery} product
   */
  function removeFromWishlist(product) {
    const data = getWishlistItemData(product);

    window._swat.removeFromWishList(data, () => {
      product.remove();
      dispatchWishlistUpdate(element);
    });
  }

  /**
   * @param {Object[]} wishlist
   * @param {Object[]} products
   * @returns {Function}
   */
  function processProductData(wishlist) {
    return function(products = []) {
      return wishlist.map(product => {
        const match = products.find(
          prod => prod.id === parseInt(product.empi, 10),
        );

        if (!match) {
          return product;
        }

        const {variants = [], available} = match;

        return {
          ...product,
          variants,
          available,
        };
      });
    };
  }

  /**
   * @param {Object[]} products
   */
  function render(products) {
    const {moveButtonLabel, chooseLabel, selectLabel, emptyMessage} = options;

    const markup = window.SwymUtils.renderTemplateString(productsListTemplate, {
      products,
      emptyMessage,
      selectLabel,
      moveButtonLabel,
      chooseLabel,
    });

    element.html(markup);
    bindEventListeners();
  }

  /**
   * @param {Object[]} products
   */
  function dispatchLoaded(products) {
    const event = new CustomEvent('wishlist:loaded', {
      detail: {
        products,
      },
      bubbles: true,
    });

    document.dispatchEvent(event);

    return products;
  }

  /**
   * @param {Object[]} wishlist
   */
  function handleWishlistFetch(wishlist) {
    const formattedWishlist = wishlist.map(formatProduct);
    const process = processProductData(formattedWishlist);
    try {
      getProductDetails(formattedWishlist)
        .then(process)
        .then(dispatchLoaded)
        .then(render)
        .catch(handleException);
    } catch (exception) {
      handleException();
    }
  }

  function handleException() {
    element.html(options.errorMessage);
  }

  function destroy() {
    removeEventListeners();
  }

  function productsCallback() {
    window._swat.fetch(
      handleWishlistFetch,
      window._swat.EventTypes.addToWishList,
    );
  }

  return {
    destroy,
  };
}
